import {
  Badge,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import moment from "moment";
import { useRecoilState } from "recoil";
import SalesListAPI from "../../../../services/SalesListApi";
import { customColors } from "../../../../atoms/customColors";
import {
  dateForAPI,
  dateForListWithHours,
} from "../../../../constants/dataFormat";
import { ToLocaleFormat } from "../../../../defaultFunctions/formaters";

// Espiada rapida nas ultimas cobrancas da assinatura, sem sair da listagem.
// A visao completa continua sendo a tela de transacoes (botao "Ver Transações").
const QUANTIDADE = 3;

// Precisa mandar o periodo explicito: sem dataInicio/dataFim a core limita a
// busca aos ultimos 5 dias e assinatura nenhuma aparece.
const PERIODO_MESES = 24;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  idCustomer?: any;
  // subtitulo do modal: hoje o plano, que e o que a listagem tem em maos
  descricao?: string;
}

function corDoStatus(status?: string) {
  const s = (status || "").toLowerCase();
  if (s.includes("sucesso") || s.includes("aprovad")) return "green";
  if (s.includes("falha") || s.includes("recusad") || s.includes("negad"))
    return "red";
  if (s.includes("cancel") || s.includes("estorn")) return "orange";
  return "gray";
}

export default function ModalUltimasCobrancas({
  isOpen,
  onClose,
  idCustomer,
  descricao,
}: Props) {
  const colors = useRecoilState(customColors)[0];
  const [cobrancas, setCobrancas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  async function carregar() {
    if (!idCustomer) return;
    try {
      setLoading(true);

      const dataInicio = moment()
        .subtract(PERIODO_MESES, "months")
        .startOf("day")
        .format(dateForAPI);
      const dataFim = moment().endOf("day").format(dateForAPI);

      // mesma consulta da tela de transações, limitada às últimas do comprador
      const response = await SalesListAPI.getSalesListVendasRecorrentes(
        1,
        QUANTIDADE,
        dataInicio,
        dataFim,
        "",
        0,
        0,
        0,
        0,
        0,
        idCustomer
      );
      setCobrancas(response.data ?? []);
    } catch (error) {
      console.log("erro ao buscar cobranças da assinatura", error);
      setCobrancas([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen) carregar();
    else setCobrancas([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idCustomer]);

  return (
    // fecha so pelo botao / X: clicar fora ou apertar ESC nao fecha
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"xl"}
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay backdropFilter="blur(6px)" />
      <ModalContent>
        <ModalHeader>
          <Text fontSize={"1rem"} fontWeight={"bold"}>
            Últimas cobranças
          </Text>
          {descricao ? (
            <Text fontSize={"0.8rem"} color={"gray.500"} fontWeight={"normal"}>
              {descricao}
            </Text>
          ) : null}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading ? (
            <Flex justifyContent={"center"} py={8}>
              <Spinner color={colors.BLUEMONEDDBUTTONS} />
            </Flex>
          ) : cobrancas.length === 0 ? (
            <Text py={6} textAlign={"center"} color={"gray.500"}>
              Nenhuma cobrança encontrada para esta assinatura.
            </Text>
          ) : (
            <Table size={"sm"} variant="simple">
              <Thead>
                <Tr>
                  <Th
                    bgColor={colors.BLUEMONEDDBUTTONS}
                    color={colors.WHITE}
                  >
                    Data
                  </Th>
                  <Th
                    bgColor={colors.BLUEMONEDDBUTTONS}
                    color={colors.WHITE}
                  >
                    Status
                  </Th>
                  <Th
                    bgColor={colors.BLUEMONEDDBUTTONS}
                    color={colors.WHITE}
                    isNumeric
                  >
                    Valor
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {cobrancas.map((item, index) => (
                  <Tr key={index}>
                    <Td>
                      {item?.dataCriacao
                        ? moment(item.dataCriacao).format(dateForListWithHours)
                        : "—"}
                    </Td>
                    <Td>
                      <Badge colorScheme={corDoStatus(item?.statusTransacao)}>
                        {item?.statusTransacao || "—"}
                      </Badge>
                    </Td>
                    <Td isNumeric>{ToLocaleFormat(item?.valor ?? 0)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            bgColor={colors.BLUEMONEDDBUTTONS}
            color={colors.WHITE}
            size={"sm"}
            onClick={onClose}
          >
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
